// components/ui/DateRangePicker.tsx
import React, { useState, useEffect } from 'react'
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { format, addDays, subDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns'

interface DateRangePickerProps {
  value: [Date | null, Date | null]
  onChange: (range: [Date | null, Date | null]) => void
  placeholder?: string
  className?: string
  minDate?: Date
  maxDate?: Date
  presets?: Array<{
    label: string
    value: [Date, Date]
  }>
  showClear?: boolean
  showPresets?: boolean
}

const defaultPresets = [
  { label: 'Today', value: [new Date(), new Date()] as [Date, Date] },
  { label: 'Yesterday', value: [addDays(new Date(), -1), addDays(new Date(), -1)] as [Date, Date] },
  { label: 'Last 7 Days', value: [addDays(new Date(), -6), new Date()] as [Date, Date] },
  { label: 'Last 30 Days', value: [addDays(new Date(), -29), new Date()] as [Date, Date] },
  { label: 'This Month', value: [startOfMonth(new Date()), endOfMonth(new Date())] as [Date, Date] },
  { label: 'Last Month', value: [startOfMonth(addDays(new Date(), -30)), endOfMonth(addDays(new Date(), -30))] as [Date, Date] }
]

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date range',
  className = '',
  minDate,
  maxDate,
  presets = defaultPresets,
  showClear = true,
  showPresets = true
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempRange, setTempRange] = useState<[Date | null, Date | null]>(value)
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [hoverDate, setHoverDate] = useState<Date | null>(null)

  // Reset temp range when value changes externally
  useEffect(() => {
    setTempRange(value)
  }, [value])

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  })

  const handleDateClick = (date: Date) => {
    if (!tempRange[0] || (tempRange[0] && tempRange[1])) {
      // Start new range
      setTempRange([date, null])
    } else if (tempRange[0] && !tempRange[1]) {
      // Complete the range
      const start = tempRange[0]
      const end = date
      const sortedRange: [Date, Date] = start < end ? [start, end] : [end, start]
      setTempRange(sortedRange)
      onChange(sortedRange)
      setIsOpen(false)
    }
  }

  const handlePresetClick = (presetRange: [Date, Date]) => {
    setTempRange(presetRange)
    onChange(presetRange)
    setIsOpen(false)
  }

  const handleClear = () => {
    setTempRange([null, null])
    onChange([null, null])
    setIsOpen(false)
  }

  const isDateSelected = (date: Date) => {
    if (!tempRange[0] || !tempRange[1]) return false
    return date >= tempRange[0] && date <= tempRange[1]
  }

  const isDateInRange = (date: Date) => {
    if (!tempRange[0] || tempRange[1]) return false
    if (!hoverDate || !tempRange[0]) return false

    const start = tempRange[0]
    const end = hoverDate
    const rangeStart = start < end ? start : end
    const rangeEnd = start < end ? end : start

    return date >= rangeStart && date <= rangeEnd
  }

  const isDateStart = (date: Date) => {
    return tempRange[0] && isSameDay(date, tempRange[0])
  }

  const isDateEnd = (date: Date) => {
    return tempRange[1] && isSameDay(date, tempRange[1])
  }

  const formatDisplayValue = () => {
    if (!value[0] && !value[1]) return placeholder
    if (value[0] && !value[1]) return format(value[0], 'MMM dd, yyyy')
    if (value[0] && value[1]) {
      if (isSameDay(value[0], value[1])) {
        return format(value[0], 'MMM dd, yyyy')
      }
      return `${format(value[0], 'MMM dd')} - ${format(value[1], 'MMM dd, yyyy')}`
    }
    return placeholder
  }

  const prevMonth = () => {
    setCurrentMonth(subDays(startOfMonth(currentMonth), 1))
  }

  const nextMonth = () => {
    setCurrentMonth(addDays(endOfMonth(currentMonth), 1))
  }

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full px-4 py-2.5 text-left bg-gray-700 border border-gray-600 
          rounded-lg text-white placeholder-gray-400 
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          flex items-center justify-between transition-all hover:bg-gray-600/50
        `}
      >
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-gray-400" />
          <span className={value[0] || value[1] ? 'text-white' : 'text-gray-400'}>
            {formatDisplayValue()}
          </span>
        </div>
        {showClear && (value[0] || value[1]) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
            className="p-1 hover:bg-gray-600 rounded"
          >
            <X size={16} className="text-gray-400" />
          </button>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl w-full max-w-2xl">
          <div className="p-4">
            {/* Presets */}
            {showPresets && (
              <div className="mb-4">
                <div className="text-sm text-gray-400 mb-2">Quick Select</div>
                <div className="flex flex-wrap gap-2">
                  {presets.map((preset, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handlePresetClick(preset.value)}
                      className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-md transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Calendar */}
            <div className="flex flex-col md:flex-row gap-6">
              {/* Month 1 */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={prevMonth}
                    className="p-2 hover:bg-gray-700 rounded"
                  >
                    <ChevronLeft size={20} className="text-gray-400" />
                  </button>
                  <div className="text-white font-medium">
                    {format(currentMonth, 'MMMM yyyy')}
                  </div>
                  <button
                    type="button"
                    onClick={nextMonth}
                    className="p-2 hover:bg-gray-700 rounded"
                  >
                    <ChevronRight size={20} className="text-gray-400" />
                  </button>
                </div>

                {/* Week days */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                    <div key={day} className="text-center text-sm text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Days */}
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day) => {
                    const isSelected = isDateSelected(day)
                    const isStart = isDateStart(day)
                    const isEnd = isDateEnd(day)
                    const isInRange = isDateInRange(day)
                    const isToday = isSameDay(day, new Date())
                    const isDisabled = 
                      (minDate && day < minDate) || 
                      (maxDate && day > maxDate)

                    return (
                      <button
                        key={day.toString()}
                        type="button"
                        onClick={() => !isDisabled && handleDateClick(day)}
                        onMouseEnter={() => !isDisabled && setHoverDate(day)}
                        onMouseLeave={() => !isDisabled && setHoverDate(null)}
                        disabled={isDisabled}
                        className={`
                          h-10 rounded text-sm font-medium transition-all
                          ${isDisabled ? 'text-gray-600 cursor-not-allowed' : 'hover:bg-gray-700'}
                          ${isToday && !isSelected && !isStart && !isEnd ? 'border border-blue-500' : ''}
                          ${isSelected && !isStart && !isEnd ? 'bg-blue-900/30 text-blue-300' : ''}
                          ${isInRange && !isStart && !isEnd ? 'bg-blue-900/20' : ''}
                          ${isStart || isEnd ? 'bg-blue-600 text-white' : ''}
                          ${!isSelected && !isInRange && !isStart && !isEnd ? 'text-gray-300' : ''}
                        `}
                      >
                        {format(day, 'd')}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Selected Range Display */}
              <div className="md:w-48">
                <div className="bg-gray-750 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-2">Selected Range</div>
                  <div className="space-y-2">
                    <div>
                      <div className="text-xs text-gray-500">From</div>
                      <div className="text-white font-medium">
                        {tempRange[0] ? format(tempRange[0], 'MMM dd, yyyy') : 'Not selected'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">To</div>
                      <div className="text-white font-medium">
                        {tempRange[1] ? format(tempRange[1], 'MMM dd, yyyy') : 'Not selected'}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (tempRange[0] && tempRange[1]) {
                            onChange(tempRange as [Date, Date])
                          }
                          setIsOpen(false)
                        }}
                        disabled={!tempRange[0] || !tempRange[1]}
                        className={`
                          flex-1 px-3 py-2 text-sm rounded-md transition-colors
                          ${tempRange[0] && tempRange[1]
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          }
                        `}
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-md transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                    {showClear && (
                      <button
                        type="button"
                        onClick={handleClear}
                        className="w-full mt-2 px-3 py-2 text-sm text-red-400 hover:bg-red-900/20 rounded-md transition-colors"
                      >
                        Clear Selection
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}